/**
 * Lucide SSR/edge icon geometry — curated map for menu tags + reservation CTAs.
 * Geometry is vendored in `lucideSsrIconData.ts` (not deep-imported from lucide-react)
 * so Turbopack never hits alias modules that omit `__iconNode` (e.g. home → house).
 */
import type { LucideSvgIconNode } from './lucideSvgIconNode';
import { isCtaLucideIconKey } from './ctaLucideCatalog';
import {
  LUCIDE_SSR_ICON_KEY_TO_NAME,
  LUCIDE_SSR_ICON_NODES,
} from './lucideSsrIconData';

export interface LucideSsrIcon {
  /** kebab-case Lucide icon name (for `lucide-${name}` class parity). */
  name: string;
  iconNode: LucideSvgIconNode;
}

function ssr(name: string): LucideSsrIcon {
  const iconNode = LUCIDE_SSR_ICON_NODES[name as keyof typeof LUCIDE_SSR_ICON_NODES];
  if (!iconNode) {
    throw new Error(`Missing vendored Lucide SSR icon node: ${name}`);
  }
  return { name, iconNode };
}

function ssrByKey(iconKey: string): LucideSsrIcon {
  const name = LUCIDE_SSR_ICON_KEY_TO_NAME[iconKey];
  if (!name) {
    throw new Error(`Missing Lucide SSR key→name mapping: ${iconKey}`);
  }
  return ssr(name);
}

export const LUCIDE_SSR_MAP_PIN: LucideSsrIcon = ssr('map-pin');
export const LUCIDE_SSR_CLOCK: LucideSsrIcon = ssr('clock');
export const LUCIDE_SSR_FILE_TEXT: LucideSsrIcon = ssr('file-text');
export const LUCIDE_SSR_CALENDAR_CHECK: LucideSsrIcon = ssr('calendar-check');
export const LUCIDE_SSR_MESSAGE_CIRCLE: LucideSsrIcon = ssr('message-circle');
export const LUCIDE_SSR_PHONE: LucideSsrIcon = ssr('phone');
export const LUCIDE_SSR_MAIL: LucideSsrIcon = ssr('mail');
export const LUCIDE_SSR_X: LucideSsrIcon = ssr('x');
export const LUCIDE_SSR_CHEVRON_LEFT: LucideSsrIcon = ssr('chevron-left');
export const LUCIDE_SSR_CHEVRON_RIGHT: LucideSsrIcon = ssr('chevron-right');

/** `iconKey` (PascalCase) → SSR geometry — menu tags + reservation CTA catalog. */
export const LUCIDE_SSR_BY_ICON_KEY: Record<string, LucideSsrIcon> = Object.fromEntries(
  Object.keys(LUCIDE_SSR_ICON_KEY_TO_NAME).map((key) => [key, ssrByKey(key)]),
);

export function resolveLucideSsrByIconKey(
  iconKey: string | null | undefined,
): LucideSsrIcon | null {
  if (!iconKey) {
    return null;
  }
  return LUCIDE_SSR_BY_ICON_KEY[iconKey] ?? null;
}

export function resolveReservationChannelSsrIcon(
  channel: 'third-party' | 'messenger' | 'call',
  iconKey: string | null | undefined,
): LucideSsrIcon {
  const fromContent =
    iconKey && isCtaLucideIconKey(iconKey) ? resolveLucideSsrByIconKey(iconKey) : null;
  if (fromContent) {
    return fromContent;
  }
  if (channel === 'messenger') {
    return LUCIDE_SSR_MESSAGE_CIRCLE;
  }
  if (channel === 'call') {
    return LUCIDE_SSR_PHONE;
  }
  return LUCIDE_SSR_CALENDAR_CHECK;
}

/** Section-header / fact / PDF CTA icon — content key or SSR fallback. */
export function resolveSectionSsrIcon(
  iconKey: string | null | undefined,
  fallback: LucideSsrIcon,
): LucideSsrIcon {
  if (iconKey && isCtaLucideIconKey(iconKey)) {
    return resolveLucideSsrByIconKey(iconKey) ?? fallback;
  }
  return fallback;
}
